import { KeyboardEvent, useCallback, useState } from 'react';

export interface SlashCommand {
  id: string;
  alias: string;
  title: string;
  description?: string;
}

interface UseSlashCommandInputReturn<T extends SlashCommand> {
  showDropdown: boolean;
  filteredCommands: T[];
  handleChange: (value: string) => string;
  handleOpenDropdown: () => void;
  handleSelectCommand: (cmd: T, currentValue: string) => string;
  handleCloseDropdown: () => void;
  handleKeyDown: (e: KeyboardEvent) => void;
}

function useSlashCommandInput<T extends SlashCommand>(
  commands: T[]
): UseSlashCommandInputReturn<T> {
  const [showDropdown, setShowDropdown] = useState(false);
  const [filter, setFilter] = useState('');

  const filteredCommands =
    filter.length > 0
      ? commands.filter((cmd) =>
          cmd.alias.toLowerCase().startsWith(filter.toLowerCase())
        )
      : commands;

  // Called on textarea onChange. Returns the value to set in the textarea.
  const handleChange = useCallback((value: string): string => {
    if (value.startsWith('/')) {
      setFilter(value.slice(1));
      setShowDropdown(true);
    } else {
      setShowDropdown(false);
      setFilter('');
    }
    return value;
  }, []);

  // Called when Commands button is clicked.
  const handleOpenDropdown = useCallback(() => {
    setFilter('');
    setShowDropdown(true);
  }, []);

  // Called when a command is selected. Returns the cleared textarea value.
  const handleSelectCommand = useCallback(
    (cmd: T, currentValue: string): string => {
      setShowDropdown(false);
      setFilter('');
      // Remove the "/" and any filter text typed so far
      if (currentValue.startsWith('/')) {
        return '';
      }
      return currentValue;
    },
    []
  );

  const handleCloseDropdown = useCallback(() => {
    setShowDropdown(false);
    setFilter('');
  }, []);

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      setShowDropdown(false);
      setFilter('');
    }
  }, []);

  return {
    showDropdown,
    filteredCommands,
    handleChange,
    handleOpenDropdown,
    handleSelectCommand,
    handleCloseDropdown,
    handleKeyDown
  };
}

export default useSlashCommandInput;
